import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { useDebouncedEffect, DateUtils } from "@grandvisionhq/utils";
import { storeFastAppointmentClickEvent, storeBookAppointmentClickEvent, storeChangeAppointmentTypeClickEvent } from "../../../../../data-layer/v3/data-layer/actions.js";
import { getClosedDates } from "../../../../AvailabilityCalendar/v3/components/AvailabilityCalendar/utils.js";
import { Variant } from "../../../../NoTimeSlots/v3/components/NoTimeSlots/types.js";
import { DropInWidget } from "./DropInWidget.js";
import useBookAppointmentStoreById from "../../../../../pages/BookAppointment/hooks/api/v3/pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js";
import useBookAppointmentType from "../../../../../pages/BookAppointment/hooks/api/v3/pages/BookAppointment/hooks/api/use-book-appointment-type.js";
import useCalendarAvailabilityById from "../../../../../hooks/api/v3/hooks/api/use-calendar-availability-by-id.js";
import { useStoreOpeningTimesById } from "../../../../../hooks/api/v3/hooks/api/use-store-opening-times-by-id.js";
import useTimeSlotsById from "../../../../../hooks/api/v3/hooks/api/use-time-slots-by-id.js";
import { useNextAvailableDate } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-next-available-date.js";
import { useNextWeekDate } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-next-week-date.js";
import { useNoTimeSlotsVariant } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-no-timeslot-variant.js";
const hasRequiredUrls = (urls) => typeof urls.bookAppointment !== "undefined";
const sanitizeUrls = (urls) => {
  const sanitizedBookAppointmentUrl = urls.bookAppointment?.replace(
    /\/+(where|what|when|who|confirmation)+/,
    ""
  );
  return { ...urls, bookAppointment: sanitizedBookAppointmentUrl };
};
const DropInWidgetContainer = ({
  urls,
  storeId,
  typeId,
  dataLayerEvents
}) => {
  const { featureConfigs } = useFeatureToggles();
  const { getFormattedLabel, getLabel } = useIntl();
  const [storeQuery, storeQueryStatus] = useBookAppointmentStoreById();
  const [typeQuery, typeQueryStatus] = useBookAppointmentType();
  const [getCalendarAvailability, calendarAvailability] = useCalendarAvailabilityById();
  const [getStoreOpeningTimes, storeOpeningTimes] = useStoreOpeningTimesById();
  const [getTimeSlots, timeSlots] = useTimeSlotsById();
  const [weekDate, setWeekDate] = React.useState();
  const [weeks, setWeeks] = React.useState();
  const [date, setDate] = React.useState();
  const [availabilityRequestParamsChanged, setAvailabilityRequestParamsChanged] = React.useState(true);
  const [timeSlot, setTimeSlot] = React.useState();
  const appointmentBookingAvailabiltyCalendarWeekCount = featureConfigs?.appointmentBookingAvailabiltyCalendarWeekCount;
  const sanitizedUrls = React.useMemo(() => sanitizeUrls(urls), [urls]);
  const calendarLoading = storeQueryStatus.loading || typeQueryStatus.loading || storeOpeningTimes.loading || calendarAvailability.loading || availabilityRequestParamsChanged;
  const loading = storeQueryStatus.loading || typeQueryStatus.loading;
  const store = storeQueryStatus.data;
  const type = typeQueryStatus.data;
  const labels = React.useMemo(
    () => ({
      title: getLabel("appointments.component.dropInWidget.title"),
      date: getLabel("appointments.component.dropInWidget.date"),
      type: getLabel("appointments.component.dropInWidget.type"),
      selectATime: getLabel("appointments.component.dropInWidget.selectATime"),
      bookAppointment: getLabel("appointments.component.dropInWidget.bookAppointment"),
      alert: getFormattedLabel("appointments.component.appointmentTypes.noAppointmentsAlert", {
        parseLink: true
      })
    }),
    [getFormattedLabel, getLabel]
  );
  const preferredTimeLabels = React.useMemo(
    () => ({
      title: getLabel("appointments.component.dropInWidget.preferredTime.title")
    }),
    [getLabel]
  );
  const closedDates = React.useMemo(() => {
    if (!weeks || !storeOpeningTimes.data) {
      return [];
    }
    return getClosedDates(weeks, storeOpeningTimes.data);
  }, [weeks, storeOpeningTimes.data]);
  useDebouncedEffect(
    () => {
      const appointmentTypeId = type?.id;
      const globalStoreId = store?.globalStoreId;
      if (!appointmentTypeId || !globalStoreId || !weeks) {
        if (process.env.NODE_ENV === "test") {
          console.log("Awaiting availability request variables data");
        }
        return;
      }
      const firstWeekDay = weeks[0]?.[0];
      const lastWeek = weeks[weeks.length - 1];
      const lastWeekDay = lastWeek?.[lastWeek.length - 1];
      if (firstWeekDay && lastWeekDay) {
        getCalendarAvailability({
          appointmentTypeId,
          globalStoreId,
          startDate: firstWeekDay,
          endDate: lastWeekDay
        });
      }
      setAvailabilityRequestParamsChanged(false);
    },
    400,
    [getCalendarAvailability, type, store, weeks]
  );
  const onCalendarWeeksChange = React.useCallback((weeks2) => {
    setAvailabilityRequestParamsChanged(true);
    setWeeks(weeks2);
  }, []);
  const onSelectDate = React.useCallback((date2) => {
    setDate(date2);
  }, []);
  const nextAvailableDate = useNextAvailableDate(
    date,
    calendarAvailability,
    Boolean(!timeSlots.called || calendarLoading)
  );
  const nextWeekDate = useNextWeekDate(weeks, !date || Boolean(nextAvailableDate));
  const noTimeSlotsVariant = useNoTimeSlotsVariant(
    timeSlots,
    nextAvailableDate,
    date,
    closedDates,
    calendarLoading
  );
  const onSelectTimeSlot = React.useCallback((timeSlot2) => setTimeSlot(timeSlot2), []);
  const onBookAppointment = React.useCallback(() => {
    if (!store) {
      return;
    }
    if (dataLayerEvents) {
      const dataLayerEvent = timeSlot && type ? storeFastAppointmentClickEvent({
        event: dataLayerEvents.storeAppointmentClickEvent,
        storeDetails: { id: store.globalStoreId, city: store.town, ...store },
        selectedDateTime: timeSlot.from,
        appointmentType: type.id.toString()
      }) : storeBookAppointmentClickEvent({
        event: dataLayerEvents.storeAppointmentClickEvent,
        storeDetails: { id: store.globalStoreId, city: store.town, ...store }
      });
      DataLayer.pushEvent(dataLayerEvent);
    }
    if (!type || !timeSlot) {
      window.location.assign(`${sanitizedUrls.bookAppointment}/what?storeId=${store.globalStoreId}`);
      return;
    }
    window.location.assign(
      `${sanitizedUrls.bookAppointment}/who?storeId=${store.globalStoreId}&typeId=${type.id}&start=${timeSlot.from.getTime()}&end=${timeSlot.until.getTime()}`
    );
  }, [dataLayerEvents, sanitizedUrls, store, type, timeSlot]);
  const onSelectedTypeContainerClick = React.useCallback(() => {
    if (!store) {
      return;
    }
    if (dataLayerEvents) {
      const dataLayerEvent = storeChangeAppointmentTypeClickEvent({
        event: dataLayerEvents.storeChangeAppointmentTypeClickEvent,
        storeDetails: { id: store.globalStoreId, city: store.town, ...store }
      });
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [store, dataLayerEvents]);
  React.useEffect(() => {
    if (!storeId) {
      return;
    }
    storeQuery(storeId);
  }, [storeId, storeQuery]);
  React.useEffect(() => {
    if (!typeId) {
      return;
    }
    typeQuery(typeId);
  }, [typeId, typeQuery]);
  React.useEffect(() => {
    if (!store) {
      return;
    }
    if (!store) {
      return;
    }
    getStoreOpeningTimes({
      variables: {
        globalStoreId: store.globalStoreId
      }
    });
  }, [getStoreOpeningTimes, store]);
  const noTimeSlotsAction = React.useCallback(() => {
    switch (noTimeSlotsVariant) {
      case Variant.date:
      case Variant.closedDate:
        if (!nextAvailableDate) {
          return;
        }
        setDate(nextAvailableDate);
        break;
      case Variant.week:
      case Variant.closedWeek:
        if (!nextWeekDate) {
          return;
        }
        setWeekDate(nextWeekDate);
        setDate(nextWeekDate);
        break;
      case Variant.error:
        const globalStoreId = store?.globalStoreId;
        const appointmentTypeId = type?.id ?? void 0;
        if (!date || globalStoreId === void 0 || appointmentTypeId === void 0) {
          return;
        }
        getTimeSlots({
          variables: {
            globalStoreId,
            appointmentTypeId,
            date: DateUtils.toLocalDate(date)
          }
        });
      // Time filter doesn't exist on SDP, as opposed to AAB.
      case Variant.time:
      case Variant.none:
        return;
    }
  }, [noTimeSlotsVariant, nextAvailableDate, nextWeekDate, getTimeSlots, store, type, date]);
  React.useEffect(() => {
    calendarAvailability.error && console.error("Error fetching appointment availability:", calendarAvailability.error.message);
  }, [calendarAvailability.error]);
  React.useEffect(() => {
    const globalStoreId = store?.globalStoreId;
    const appointmentTypeId = type?.id ?? void 0;
    if (!date || globalStoreId === void 0 || appointmentTypeId === void 0) {
      return;
    }
    getTimeSlots({
      variables: {
        globalStoreId,
        appointmentTypeId,
        date: DateUtils.toLocalDate(date)
      }
    });
  }, [getTimeSlots, store, type, date]);
  if (!hasRequiredUrls(sanitizedUrls)) {
    console.error("Cannot render DropInWidget, missing required urls.");
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    DropInWidget,
    {
      urls: sanitizedUrls,
      loading,
      labels,
      store,
      type,
      onCalendarWeeksChange,
      selectedDate: date,
      onSelectDate,
      calendarLoading,
      calendarWeekDate: weekDate,
      calendarWeekCount: appointmentBookingAvailabiltyCalendarWeekCount,
      calendarClosedDates: closedDates,
      calendarAvailability: calendarAvailability.data,
      timeSlotsLoading: timeSlots.loading,
      timeSlots: timeSlots.filtered.included,
      onSelectTime: onSelectTimeSlot,
      selectedTimeSlot: timeSlot,
      noTimeSlotsVariant,
      noTimeSlotsAction,
      nextAvailableDate,
      noMoreAvailabilityUntilDate: nextWeekDate,
      onBookAppointment,
      preferredTimeLabels,
      onSelectedTypeContainerClick
    }
  );
};
export {
  DropInWidgetContainer,
  DropInWidgetContainer as default
};
//# sourceMappingURL=DropInWidgetContainer.js.map
